import * as React from 'react'
import {
  Show,
  SimpleShowLayout,
  TextField,
  EmailField,
  NumberField,
  BooleanField,
  FunctionField,
  EditButton,
  TopToolbar,
  TabbedShowLayout,
  ReferenceField,
  useShowContext,
  Loading
} from 'react-admin'
import { Box, Grid } from '@mui/material'
import { formatDateToMMDDYYYY, fromTimestamp, formatPhoneDisplay } from '../../utils/helpers'

const KitRequestTitle = () => {
  return <span>Kit Request Details</span>
}

const ShowActions = () => (
  <TopToolbar>
    <EditButton />
  </TopToolbar>
)



const DetailsContent = () => {
  const { record, isLoading } = useShowContext()
  if (isLoading || !record) return <Loading />

  return (
    <>
      <Box sx={{ flexGrow: 1, border: '2px solid black', padding: '16px' }}>
        <Grid container spacing={1}>
          <Grid item xs={3}>
            <SimpleShowLayout spacing={1} className="details-page-layout">
              <TextField label="Status" source="requestStatus" />
            </SimpleShowLayout>
          </Grid>
          <Grid item xs={3}>
            <SimpleShowLayout spacing={1} className="details-page-layout">
              <FunctionField
                label="Patient Name"
                render={(record) => `${record.patientFirstName || ''} ${record.patientLastName || ''}`}
              />
            </SimpleShowLayout>
          </Grid>
          <Grid item xs={3}>
            <SimpleShowLayout spacing={1} className="details-page-layout">
              <ReferenceField label="Provider" source="providerId" reference="providers" link="show">
                <TextField source="displayName" />
              </ReferenceField>
            </SimpleShowLayout>
          </Grid>
          <Grid item xs={3}>
            <SimpleShowLayout spacing={1} className="details-page-layout">
              <FunctionField label="Created At" render={(record) => fromTimestamp(record.createdAt) || '—'} />
            </SimpleShowLayout>
          </Grid>
        </Grid>
      </Box>

      <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', marginBottom: '1rem' }}></div>

      <TabbedShowLayout spacing={2}>
        <TabbedShowLayout.Tab label="Patient">
          <TextField label="First Name" source="patientFirstName" />
          <TextField label="Last Name" source="patientLastName" />
          <EmailField source="email" label="Email" />
          <FunctionField
            label="Phone Number"
            source="phoneNumber"
            render={(record) => formatPhoneDisplay(record.phoneNumber) || '—'}
          />
          <FunctionField
            label="Date of Birth"
            render={(record) => formatDateToMMDDYYYY(record.patientDob)}
          />
          <FunctionField
            label="Date of Vasectomy"
            render={(record) => formatDateToMMDDYYYY(record.dateOfVasectomy)}
          />
        </TabbedShowLayout.Tab>

        <TabbedShowLayout.Tab label="Shipping">
          <TextField source="shippingAddress1" label="Address 1" />
          <TextField source="shippingAddress2" label="Address 2" />
          <TextField source="shippingCity" label="City" />
          <TextField source="shippingState" label="State" />
          <TextField source="shippingZip" label="ZIP" />
        </TabbedShowLayout.Tab>

        <TabbedShowLayout.Tab label="Provider & Hierarchy">
          <ReferenceField label="Provider Group" source="providerGroupId" reference="provider-groups" link="show">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField label="Provider Location" source="providerLocationId" reference="provider-locations" link="show">
            <TextField source="locationName" />
          </ReferenceField>
          <ReferenceField label="Provider" source="providerId" reference="providers" link="show">
            <TextField source="displayName" />
          </ReferenceField>
          <ReferenceField label="Provider Access User" source="providerAccessId" reference="provider-access/users" link={false} emptyText="N/A">
            <TextField source="email" />
          </ReferenceField>
          <TextField source="clinicName" label="Clinic Name (Legacy)" />
          <TextField source="providerName" label="Provider Name" />
        </TabbedShowLayout.Tab>

        <TabbedShowLayout.Tab label="Financials">
          <ReferenceField label="Contract" source="providerContractId" reference="provider-contracts" link="show">
            <FunctionField render={c => `${c.startDate} to ${c.endDate || 'Present'}`} />
          </ReferenceField>

          <TextField source="shippedFinancialStatus" label="Shipped Status" />
          <NumberField source="providerShippedAmount" label="Provider Shipped Amount" options={{ style: 'currency', currency: 'USD' }} />
          <NumberField source="customerAmount" label="Patient Charged" options={{ style: 'currency', currency: 'USD' }} />
          <TextField source="shippedInvoiceId" label="Shipped Invoice ID" />

          <TextField source="testedFinancialStatus" label="Tested Status" />
          <NumberField source="providerTestedAmount" label="Provider Tested Amount" options={{ style: 'currency', currency: 'USD' }} />
          <TextField source="testedInvoiceId" label="Tested Invoice ID" />
        </TabbedShowLayout.Tab>

        <TabbedShowLayout.Tab label="Fulfillment">
          <TextField source="orderId" label="Shopify Order ID" />
          <TextField source="shopifyOrderNumber" label="Shopify Order Number" />
          <TextField source="kitId" label="MFH Kit ID" />
          <TextField source="convertDate" label="Convert Date" />
          <FunctionField
            label="Target Convert Date"
            render={(record) => formatDateToMMDDYYYY(record.targetConvertDate)}
          />
          <TextField source="shippedDate" label="Shipped Date" />
          <TextField source="testedDate" label="Tested Date" />
          <TextField source="convertFailureReason" label="Convert Failure Reason" emptyText="—" />
          <TextField source="convertAttemptCount" label="Convert Attempts" emptyText="0" />
          <TextField source="lastConvertAttemptAt" label="Last Convert Attempt" emptyText="—" />
        </TabbedShowLayout.Tab>

        <TabbedShowLayout.Tab label="Other">
          <TextField source="note" label="Notes" />
          <BooleanField source="agreedToTerms" label="Agreed to Terms" />
          <TextField source="notificationMethod" label="Notification Method" />
          <TextField source="language" label="Language" />
          <FunctionField
            label="7-Day Reminder Sent"
            render={(record) => formatDateToMMDDYYYY(record.sevenDayReminderDate)}
          />
          <FunctionField
            label="21-Day Reminder Sent"
            render={(record) => formatDateToMMDDYYYY(record.twentyOneDayReminderDate)}
          />
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </>
  )
}

export const KitRequestDetails = () => (
  <Show title={<KitRequestTitle />} actions={<ShowActions />}>
    <DetailsContent />
  </Show>
)

export default KitRequestDetails
